import type { ReviewTaskKind, ReviewTaskStatus } from '@platform/contracts';
import type { BadgeTone } from '@platform/ui';

/**
 * The review queue's vocabulary (MASTER_PLAN M6.9 UI, FR-INS-09).
 *
 * `review_tasks.kind` is a database enum and reads like one. The queue is worked by a
 * receptionist between phone calls, so every value gets a sentence: what happened, and
 * what the person looking at the row is being asked to do about it.
 *
 * `Record<Enum, …>` so a kind added to the contract is a compile error here rather than a
 * raw `undeliverable_contact` on a screen.
 */

/** The keys the queue reads, spelled as `Docs/API.md` §2.6 spells them. */
export const REVIEW_PERMISSIONS = {
  /** Opens `/review` and covers every action on it. */
  review: 'insurance.review',
  /** `POST /admin/appointments/:id/eligibility/rerun`, from the appointment page. */
  check: 'insurance.check',
} as const;

export const REVIEW_KIND_LABELS: Record<ReviewTaskKind, string> = {
  price_review: 'Price to confirm',
  eligibility_failed: 'Coverage check failed',
  ehr_failed: 'EHR write failed',
  charge_failed: 'Charge failed',
  undeliverable_contact: 'Cannot reach the patient',
  manual_completion: 'Visit to close by hand',
  late_cancel_fee: 'Late cancellation fee',
  /* M15: the scheduling rules asked a patient to contact the office. */
  scheduling_blocked: 'Patient asked to call',
};

export const REVIEW_KIND_HELP: Record<ReviewTaskKind, string> = {
  price_review:
    'The engine could not price this booking on its own, or the amount was at or above your review threshold. Decide what the patient owes.',
  eligibility_failed:
    'The payer rejected the details, or did not answer. Check the member id and run it again, or price the visit another way.',
  ehr_failed: 'The appointment did not reach the EHR. Retry the sync or enter it by hand.',
  charge_failed: 'The card was declined or the processor errored. Contact the patient or void it.',
  undeliverable_contact:
    'An email or message bounced. Get a working address before the reminders are due.',
  manual_completion:
    'The visit never moved to completed on its own. Close it so the charge can run.',
  late_cancel_fee:
    'A cancellation inside the policy window. Apply the fee, or waive it with a reason.',
  scheduling_blocked:
    'The scheduling rules stopped an online booking and asked the patient to contact the office. Call them back and book in the EHR.',
};

export const REVIEW_STATUS_LABELS: Record<ReviewTaskStatus, string> = {
  open: 'Open',
  in_progress: 'Being worked on',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

export const REVIEW_STATUS_TONES: Record<ReviewTaskStatus, BadgeTone> = {
  open: 'warning',
  in_progress: 'info',
  resolved: 'success',
  dismissed: 'neutral',
};

/** A closed task takes no further action; the drawer shows the resolution instead. */
export function isClosed(status: ReviewTaskStatus): boolean {
  return status === 'resolved' || status === 'dismissed';
}
